import axios from 'axios'
import { api } from './api'
import type {
  Auth0Authenticator,
  AuthTokenResponse,
  AuthUserResponse,
  EmailChallengeConfirmPayload,
  MfaAssociateResponse,
  MfaChallengePayload,
  MfaChallengeResponse,
  OtpConfirmPayload,
  OtpEnrollConfirmPayload,
  RegisterPayload,
  ResetPasswordPayload,
} from '../types/auth.types'

const auth0Domain = () => import.meta.env.VITE_AUTH0_DOMAIN as string
const auth0ClientId = () => import.meta.env.VITE_AUTH0_CLIENT_ID as string
const auth0Audience = () => import.meta.env.VITE_AUTH0_AUDIENCE as string
const MFA_OTP_GRANT = 'http://auth0.com/oauth/grant-type/mfa-otp'
const MFA_OOB_GRANT = 'http://auth0.com/oauth/grant-type/mfa-oob'


export const auth = async (): Promise<AuthUserResponse> => {
  const res = await api.get<AuthUserResponse>('user/auth/')
  return res.data
}

export const checkInvite = async (invite: string) => {
  const res = await api.get(`user/invite/${invite}/`)
  return res.data
}

export const registerWithInvite = async (data: RegisterPayload) => {
  const formData = new FormData()
  formData.append('first_name', data.first_name)
  formData.append('last_name', data.last_name)
  formData.append('password', data.password)
  if (data.avatar) {
    formData.append('avatar', data.avatar)
  }
  const res = await api.post(`user/invite/${data.invite}/`, formData)
  return res.data
}

export const disableUser = async () => {
  const res = await api.delete('user/delete')
  return res.data
}

export const sendPwResetEmail = async (email: string) => {
  const res = await api.post('user/pw_reset/', { email })
  return res.data
}

export const resetMFA = async () => {
  const res = await api.post('user/mfa_reset/')
  return res.data
}

export const checkPwReset = async (token: string) => {
  const res = await api.get(`user/pw_reset/${token}/`)
  return res.data
}

export const resetPw = async (data: ResetPasswordPayload) => {
  const res = await api.post(`user/pw_reset/${data.token}/`, data)
  return res.data
}

// MFA — Auth0 direct calls (bypass the api instance and its interceptors)

export const getAuthenticators = async (mfa_token: string): Promise<Auth0Authenticator[]> => {
  const res = await axios.get<Auth0Authenticator[]>(`https://${auth0Domain()}/mfa/authenticators`, {
    headers: { Authorization: `Bearer ${mfa_token}` },
  })
  return res.data
}

export const triggerChallenge = async (payload: MfaChallengePayload): Promise<MfaChallengeResponse> => {
  const res = await axios.post<MfaChallengeResponse>(`https://${auth0Domain()}/mfa/challenge`, {
    client_id: auth0ClientId(),
    mfa_token: payload.mfa_token,
    authenticator_id: payload.authenticator_id,
    challenge_type: payload.challenge_type,
  })
  return res.data
}

export const confirmOtp = async (payload: OtpConfirmPayload): Promise<AuthTokenResponse> => {
  const res = await axios.post<AuthTokenResponse>(`https://${auth0Domain()}/oauth/token`, {
    grant_type: MFA_OTP_GRANT,
    client_id: auth0ClientId(),
    audience: auth0Audience(),
    mfa_token: payload.mfa_token,
    otp: payload.otp,
    scope: 'openid profile email offline_access',
  })
  return res.data
}

export const confirmEmailChallenge = async (payload: EmailChallengeConfirmPayload): Promise<AuthTokenResponse> => {
  const res = await axios.post<AuthTokenResponse>(`https://${auth0Domain()}/oauth/token`, {
    grant_type: MFA_OOB_GRANT,
    client_id: auth0ClientId(),
    audience: auth0Audience(),
    mfa_token: payload.mfa_token,
    oob_code: payload.oob_code,
    binding_code: payload.binding_code,
    scope: 'openid profile email offline_access',
  })
  return res.data
}

export const enrollAuthenticator = async (mfa_token: string): Promise<MfaAssociateResponse> => {
  const res = await axios.post(
    `https://${auth0Domain()}/mfa/associate`,
    { authenticator_types: ['otp'] },
    { headers: { Authorization: `Bearer ${mfa_token}` } },
  )
  // Auth0 returns `secret`; the UI expects `client_secret`. Normalise here.
  return {
    authenticator_type: res.data.authenticator_type,
    barcode_uri: res.data.barcode_uri,
    client_secret: res.data.secret ?? res.data.client_secret,
    recovery_codes: res.data.recovery_codes,
  }
}

export const confirmEnrollment = async (payload: OtpEnrollConfirmPayload): Promise<AuthTokenResponse> => {
  const res = await axios.post<AuthTokenResponse>(`https://${auth0Domain()}/oauth/token`, {
    grant_type: MFA_OTP_GRANT,
    client_id: auth0ClientId(),
    audience: auth0Audience(),
    mfa_token: payload.mfa_token,
    otp: payload.otp,
    scope: 'openid profile email offline_access',
  })
  return res.data
}
